'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import gsap from 'gsap';
import { Link } from '@/i18n/navigation';
import { getLenis } from '@/lib/lenisInstance';
import { INTRO_HUD, HIJACK } from '@/lib/motionTokens';
import { getJourneyPinTrigger } from '@/lib/journeyNav';
import { INTRO_PHASE_VH } from '@/lib/motionTokens';

/**
 * HTML overlay rendered as a sibling of the Canvas — appears instantly,
 * does not wait on R3F or any GLB. The title sits at the top of the
 * viewport; the tagline and the scroll cue sit at the bottom so they
 * frame the `.W` mark in the centre.
 *
 * Both groups gently fade and translate as the user scrolls into the
 * journey (handled inline below — no scroll listeners needed, the
 * IntroLogoBreak's rAF tick covers parallax timing for the W itself).
 */
export function IntroScene() {
  const t = useTranslations('Journey');
  const cueRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLDivElement | null>(null);
  const cueWrapperRef = useRef<HTMLDivElement | null>(null);
  const ctaRef = useRef<HTMLDivElement | null>(null);

  // "Explorar aqui" — drive the same descent a manual scroll would: glide
  // down through the explosion window so the logo break plays, then cross the
  // intro-exit auto-snap trigger, which hijacks to the first exhibit and
  // hands control to the corridor's station snap. Deliberately NO `lock` — a
  // locked Lenis scroll persists and blocks that hijack's snaps (the user
  // would land in the journey but scroll freely instead of iframe-to-iframe).
  function descendIntoJourney() {
    const trigger = getJourneyPinTrigger();
    if (!trigger) return;
    const introEnd = trigger.start + (INTRO_PHASE_VH * window.innerHeight) / 100;
    const lenis = getLenis();
    if (lenis) {
      lenis.scrollTo(introEnd, { duration: HIJACK.introDescent.travelSeconds });
    } else {
      window.scrollTo({ top: introEnd, behavior: 'smooth' });
    }
  }

  useEffect(() => {
    if (!titleRef.current || !cueRef.current || !cueWrapperRef.current) return;

    const titleTween = gsap.from(titleRef.current, {
      y: INTRO_HUD.title.fromY,
      opacity: 0,
      duration: INTRO_HUD.title.durationSeconds,
      ease: INTRO_HUD.title.ease,
    });

    const bounceTween = gsap.to(cueRef.current, {
      y: INTRO_HUD.cueBounce.amplitudePx,
      repeat: -1,
      yoyo: true,
      duration: INTRO_HUD.cueBounce.durationSeconds,
      ease: INTRO_HUD.cueBounce.ease,
    });

    // Single source of truth for the cue's opacity. opacity = fadeIn ×
    // (1 − scrollFade), so the mount fade-in and the scroll-driven fade live
    // in the same equation — scrolling back to the top of the journey
    // automatically restores opacity to 1.
    const FADE_IN_DELAY_MS = INTRO_HUD.tagline.delaySeconds * 1000;
    const FADE_IN_DURATION_MS = INTRO_HUD.tagline.durationSeconds * 1000;
    const FADE_OUT_START_PX = INTRO_HUD.exitFade.scrollThresholdPx;
    const FADE_OUT_RANGE_PX = 110;

    const startTime = performance.now();
    let rafId = 0;

    const tick = () => {
      rafId = requestAnimationFrame(tick);
      const wrapper = cueWrapperRef.current;
      if (!wrapper) return;
      const elapsed = performance.now() - startTime;
      const fadeInRaw = Math.min(1, Math.max(0, (elapsed - FADE_IN_DELAY_MS) / FADE_IN_DURATION_MS));
      const fadeIn = fadeInRaw * fadeInRaw * (3 - 2 * fadeInRaw);
      const y = window.scrollY;
      const fadeOutRaw = (y - FADE_OUT_START_PX) / FADE_OUT_RANGE_PX;
      const fadeOut = Math.min(1, Math.max(0, fadeOutRaw));
      const opacity = fadeIn * (1 - fadeOut);
      wrapper.style.opacity = String(opacity);
      // The CTA chips are interactive, so unlike the decorative cue they must
      // drop out of pointer + tab reach once faded. `inert` covers both in one
      // property — no React state tied to scroll.
      const cta = ctaRef.current;
      if (cta) cta.inert = opacity < 0.5;
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      titleTween.kill();
      bounceTween.kill();
    };
  }, []);

  return (
    <>
      {/* Top — brand title */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-col items-center pt-28">
        <div ref={titleRef} className="text-center">
          <p className="text-[0.55rem] uppercase tracking-[8px] text-(--color-accent)/60">Wantek</p>
          <h1 className="mt-2 text-3xl font-extrabold uppercase tracking-widest text-(--color-muted)">
            {t('placeholderTitle')}
          </h1>
        </div>
      </div>
      {/* Scroll cue — sits just below the exploding .W mark. The mark's
          stage is centred and ~34vh tall (capped at 300px), so its bottom
          edge is at roughly `50% + min(17vh, 150px)`. We anchor the cue a
          touch below that so it reads as "below the logo" on any viewport.
          `fixed` (not `absolute`) so it stays in view once ScrollTrigger
          inserts the ~1400vh pin-spacer into the section. */}
      <div
        ref={cueWrapperRef}
        style={{ opacity: 0, top: 'calc(50% + min(15vh, 140px))' }}
        className="pointer-events-none fixed inset-x-0 z-10 flex flex-col items-center gap-8"
      >
        {/* Two equal entry points — descend the 3D corridor here, or jump to
            the flat grid. The group is the only pointer-interactive part of
            the otherwise pass-through overlay; `inert` is toggled on scroll. */}
        <div ref={ctaRef} className="pointer-events-auto flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={descendIntoJourney}
            className="font-mono text-[0.62rem] uppercase tracking-[3px] text-(--color-muted) border border-(--color-muted-foreground)/30 px-4 py-2.5 rounded-sm transition-[scale,border-color,color] duration-[var(--duration-feedback)] ease-[var(--ease-feedback)] hover:scale-[1.03] hover:border-(--color-accent) hover:text-(--color-accent)"
          >
            {t('ctaExplore')}
          </button>
          <Link
            href="/grid"
            className="font-mono text-[0.62rem] uppercase tracking-[3px] text-(--color-muted) border border-(--color-muted-foreground)/30 px-4 py-2.5 rounded-sm transition-[scale,border-color,color] duration-[var(--duration-feedback)] ease-[var(--ease-feedback)] hover:scale-[1.03] hover:border-(--color-accent) hover:text-(--color-accent)"
          >
            {t('ctaGrid')}
          </Link>
        </div>
        <div
          ref={cueRef}
          className="flex flex-col items-center text-(--color-muted-foreground)"
        >
          <span aria-hidden className="font-mono text-base opacity-70">↓</span>
        </div>
      </div>
    </>
  );
}
